import { Navigate, useLocation, useParams } from 'react-router-dom';

// Compatibility redirect for a renamed route (P03 §0/§6: "`/app/properties`
// remains a compatibility redirect for one release"; story 3-1b).
//
// `<Navigate to="/app/sites" replace/>` — the obvious thing — is WRONG here and
// silently so: it drops `location.search`, so a bookmarked
// `/app/properties?q=ocean&status=archived` lands on an unfiltered list, and it
// cannot carry a `:id`, so a deep link to one record lands on the index. P03
// §7.1 requires the opposite in both cases: *"legacy deep links redirect to
// `/app/sites` WITHOUT losing filters or record identity."*
//
// So this component rebuilds the destination from the parts that must survive:
//   * `:id` (and any other path param named in `to` as `:param`) — record
//     identity. 3-1a preserved ids (`subjectId === propertyId`), so the SAME id
//     addresses the same record on the new route; no lookup or mapping table
//     is involved, which is precisely why that decision was worth making.
//   * `location.search` — every filter, verbatim, including ones this release
//     does not know about (a future filter added to the Sites list keeps
//     working through old links for free).
//   * `location.hash`.
// `replace` so Back returns to wherever the user came FROM, not to a legacy URL
// that would immediately bounce them forward again.

export interface LegacyRedirectProps {
  /** Destination path; `:param` segments are filled from the matched route. */
  to: string;
}

export function LegacyRedirect({ to }: LegacyRedirectProps): JSX.Element {
  const params = useParams();
  const location = useLocation();
  const target = to.replace(/:([A-Za-z0-9_]+)/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : encodeURIComponent(value);
  });
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
}
